/* eslint-disable camelcase */
import { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  InputBase,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
} from '@mui/material'
import {
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  AlternateEmail as AlternateEmailIcon,
  LocalPhone as LocalPhoneIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material'
import { useDataProvider, useRedirect } from 'react-admin'
import { xxllncColor } from '../../layout/themes'
import { StyledBox, classes } from './ContactPanelStyles'

interface Contact {
  id: string;
  name: string;
  first_name?: string;
  surname?: string;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  bsn?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
}

interface ContactPanelProps {
  contactUuid: string | null;
  contactName: string | null;
  onLinkContact: (contactUuid: string, contactName: string) => void;
}

interface Conversation {
  id: string;
  sid: number;
  channel: string;
  status: string;
  subject?: string | null;
  contact_uuid?: string | null;
  contact_name?: string | null;
  case_uuid?: string | null;
  case_number?: string | null;
}

const ContactPanel = ({
  contactUuid,
  contactName,
  onLinkContact,
}: ContactPanelProps) => {
  const dataProvider = useDataProvider()
  const redirect = useRedirect()
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<Contact[]>([])
  const [showResults, setShowResults] = useState(false)
  const [contactDetail, setContactDetail] = useState<Contact | null>(null)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [expanded, setExpanded] = useState(false)
  const [showBsn, setShowBsn] = useState(false)

  const handleConversationClick = (id: string) => {
    redirect(`/conversation/${id}`)
  }

  useEffect(() => {
    if (!contactUuid) {
      setContactDetail(null)
      setConversations([])
      setExpanded(false)
      return
    }

    const loadContactData = async () => {
      try {
        const { data: detail } = await dataProvider.getOne<Contact>('contact', {
          id: contactUuid,
        })
        setContactDetail(detail)

        const { data: convData } = await dataProvider.getList<Conversation>(
          'conversation',
          {
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'sid', order: 'DESC' },
            filter: { contact_uuid: contactUuid },
          },
        )
        setConversations(convData)
      } catch (e) {
        console.error('Failed to load contact data:', e)
      }
    }
    void loadContactData()
  }, [contactUuid, dataProvider])

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) return
    try {
      const { data } = await dataProvider.getList<Contact>('contact', {
        pagination: { page: 1, perPage: 25 },
        sort: { field: 'name', order: 'ASC' },
        filter: { keyword: searchValue },
      })
      setSearchResults(data)
      setShowResults(true)
    } catch (e) {
      console.error('Contact search failed:', e)
    }
  }, [dataProvider, searchValue])

  const handleSelectContact = (contact: Contact) => {
    onLinkContact(contact.id, contact.name)
    setShowResults(false)
    setSearchResults([])
    setSearchValue('')
    setExpanded(false)
  }

  const initials = contactName
    ? contactName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
    : '??'

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return ''
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('nl-NL')
    } catch {
      return dateStr || ''
    }
  }

  return (
    <StyledBox>
      <Box sx={{ position: 'relative' }}>
        <Paper className={classes.card}>
          {!contactUuid ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box className={classes.avatarAnon}>
                <Typography sx={{ fontWeight: 'bold' }}>??</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Anoniem
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box className={classes.avatar}>{initials}</Box>
                <Typography variant="h5" sx={{ fontWeight: 500 }}>
                  {contactName}
                </Typography>
              </Box>

              {contactDetail && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {contactDetail.bsn
                          ? `BSN ${showBsn ? contactDetail.bsn : '*** *** ***'}`
                          : ''}
                      </Typography>
                      {contactDetail.bsn && (
                        <IconButton
                          size="small"
                          onClick={() => setShowBsn(!showBsn)}
                          sx={{ ml: 1, p: 0, color: 'inherit' }}
                        >
                          {showBsn ? (
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <VisibilityOffIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {contactDetail.date_of_birth
                        ? formatDate(contactDetail.date_of_birth)
                        : ''}
                      {contactDetail.date_of_birth && contactDetail.gender
                        ? ' | '
                        : ''}
                      {contactDetail.gender || ''}
                    </Typography>
                  </Box>

                  <Collapse in={expanded}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                        ml: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'flex-start',
                        }}
                      >
                        <HomeIcon
                          sx={{
                            color: 'text.secondary',
                            fontSize: 20,
                            mt: 0.3,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#333' }}>
                          {contactDetail.address || 'Geen adres beschikbaar'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
                      >
                        <LocalPhoneIcon
                          sx={{ color: 'text.secondary', fontSize: 20 }}
                        />
                        <Typography variant="body2" sx={{ color: '#333' }}>
                          {contactDetail.phone
                            ? contactDetail.phone.startsWith('+')
                              ? contactDetail.phone
                              : `+${contactDetail.phone}`
                            : 'Geen telefoonnummer'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
                      >
                        <AlternateEmailIcon
                          sx={{ color: 'text.secondary', fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: xxllncColor.veniceBlue,
                            textDecoration: 'underline',
                            wordBreak: 'break-all',
                            lineHeight: 1.2,
                          }}
                        >
                          {contactDetail.email || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mt: 0.5,
                    }}
                  >
                    <IconButton
                      onClick={() => setExpanded(!expanded)}
                      sx={{
                        'bgcolor': 'white',
                        'boxShadow': '0px 2px 8px rgba(0,0,0,0.15)',
                        'width': 32,
                        'height': 32,
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon
                          sx={{ color: xxllncColor.veniceBlue, fontSize: 20 }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ color: xxllncColor.veniceBlue, fontSize: 20 }}
                        />
                      )}
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          )}

          {!contactUuid && (
            <Paper className={classes.searchBar} sx={{ mt: 2 }}>
              <InputBase
                sx={{ ml: 1.5, flex: 1 }}
                placeholder="Zoek persoon..."
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                  if (showResults) setShowResults(false)
                }}
                onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
              />
              <IconButton
                className={classes.searchButton}
                onClick={() => {
                  void handleSearch()
                }}
              >
                <SearchIcon />
              </IconButton>
            </Paper>
          )}
        </Paper>

        {showResults && !contactUuid && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
              bgcolor: 'white',
            }}
          >
            {searchResults.length > 0 ? (
              <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
                {searchResults.map((contact, index) => (
                  <Box key={contact.id}>
                    <ListItem
                      button
                      onClick={() => handleSelectContact(contact)}
                      sx={{ py: 1 }}
                    >
                      <PersonIcon
                        sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }}
                      />
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              color: xxllncColor.veniceBlue,
                              textDecoration: 'underline',
                              fontSize: '0.9rem',
                              fontWeight: 500,
                            }}
                          >
                            {contact.name}
                          </Typography>
                        }
                        secondary={contact.type || 'Contactpersoon'}
                      />
                    </ListItem>
                    {index < searchResults.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </Box>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Geen resultaten gevonden voor "{searchValue}"
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {contactUuid && (
        <Box className={classes.scrollContainer}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
            Eerdere contacten
          </Typography>
          {conversations.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {conversations.map((conv) => (
                <Box
                  key={conv.id}
                  onClick={() => handleConversationClick(conv.id)}
                  sx={{
                    'display': 'flex',
                    'justifyContent': 'space-between',
                    'alignItems': 'flex-start',
                    'cursor': 'pointer',
                    'padding': '4px 0',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <LocalPhoneIcon
                      sx={{
                        color: xxllncColor.mineShaft,
                        fontSize: 18,
                        mt: 0.3,
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.2,
                      }}
                    >
                      <Typography
                        sx={{
                          textDecoration: 'underline',
                          fontWeight: 500,
                          fontSize: '0.95rem',
                        }}
                      >
                        {conv.subject || `Gesprek #${conv.sid}`}
                      </Typography>

                      {conv.case_number && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.3,
                          }}
                        >
                          <FolderOpenIcon
                            sx={{ color: 'text.secondary', fontSize: 14 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 500 }}
                          >
                            Zaak: {conv.case_number}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 0.3 }}
                  >
                    {conv.status === 'completed' ? 'Afgerond' : 'Actief'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Geen eerdere contacten gevonden.
            </Typography>
          )}
        </Box>
      )}
    </StyledBox>
  )
}

export default ContactPanel